import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {useParams, Link as RouterLink} from 'react-router-dom';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {
    Box, Typography, Paper, Grid, Stack, Avatar, Chip, Divider,
    CircularProgress, Alert, Button, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import apiService from '../../services/apiService';
import {ROLES} from '../../config/roles';

const StatItem = ({label, value}) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{fontWeight: 'bold'}}>{label}:</Typography>
        <Typography variant="body2">{value}</Typography>
    </Stack>
);

const SummaryCard = ({detail}) => (
    <Paper elevation={2} sx={{p: 2, mb: 3}}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}}>
            <Avatar src={detail.iconUrl} alt={detail.name} sx={{width: 48, height: 48}}/>
            <Box sx={{flex: 1}}>
                <Typography variant="h5">{detail.name}</Typography>
                <Typography variant="body2" color="text.secondary">{detail.identifier}</Typography>
            </Box>
            <Chip
                label={detail.enabled ? 'Enabled' : 'Disabled'}
                color={detail.enabled ? 'success' : 'default'}
                size="small"
            />
            {detail.identifier === 'hilo' && (
                <Button
                    component={RouterLink}
                    to={`/minigames/stats/${detail.minigameId}/hilo`}
                    variant="contained"
                    size="small"
                    startIcon={<BarChartIcon/>}
                >
                    HiLo Analytics
                </Button>
            )}
            <Button
                component={RouterLink}
                to={`/minigames/${detail.minigameId}`}
                variant="outlined"
                size="small"
                startIcon={<EditIcon/>}
            >
                Edit
            </Button>
        </Stack>
        <Divider sx={{mb: 2}}/>
        <Grid container spacing={3}>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Total Plays</Typography>
                    <Typography variant="h5">{detail.totalPlays.toLocaleString()}</Typography>
                </Box>
            </Grid>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Total Wins</Typography>
                    <Typography variant="h5">{detail.totalWins.toLocaleString()}</Typography>
                </Box>
            </Grid>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                    <Typography variant="h5">{detail.winRate.toFixed(1)}%</Typography>
                </Box>
            </Grid>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Jackpots</Typography>
                    <Typography variant="h5">{detail.totalJackpots.toLocaleString()}</Typography>
                </Box>
            </Grid>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Jackpot Rate</Typography>
                    <Typography variant="h5">{detail.jackpotRate.toFixed(2)}%</Typography>
                </Box>
            </Grid>
            <Grid size={{xs: 6, sm: 4, md: 2}}>
                <Box sx={{textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">Unique Players</Typography>
                    <Typography variant="h5">{detail.uniquePlayers.toLocaleString()}</Typography>
                </Box>
            </Grid>
        </Grid>
    </Paper>
);

const LeaderboardTable = ({title, data, valueColumn, valueLabel}) => {
    const columns = useMemo(() => [
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {accessorKey: 'totalPlays', header: 'Plays', size: 80},
        {accessorKey: valueColumn, header: valueLabel, size: 80},
        {accessorKey: 'totalSpent', header: 'Spent', size: 80},
    ], [valueColumn, valueLabel]);

    const table = useMaterialReactTable({
        columns,
        data,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableSorting: false,
        muiTableContainerProps: {sx: {maxHeight: 400}},
    });

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Divider sx={{mb: 1}}/>
            {data.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{py: 2}}>No data yet.</Typography>
            ) : (
                <MaterialReactTable table={table}/>
            )}
        </Paper>
    );
};

const PlayLogTable = ({minigameId}) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 20});

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });
        apiService.get(`/api/minigames/stats/${minigameId}/plays?${params}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [minigameId, pagination]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            accessorKey: 'createdAt',
            header: 'Time',
            size: 170,
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—',
        },
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {accessorKey: 'eventType', header: 'Event', size: 90},
        {
            accessorKey: 'eventOutput',
            header: 'Result',
            Cell: ({cell}) => {
                const v = cell.getValue();
                if (!v) return '—';
                if (v.combination) return v.combination;
                return JSON.stringify(v).substring(0, 60);
            },
        },
        {
            accessorKey: 'isJackpot',
            header: 'Jackpot',
            size: 90,
            Cell: ({cell}) => cell.getValue()
                ? <Chip label="JACKPOT" color="warning" size="small"/>
                : null,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        rowCount,
        state: {pagination, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading play log'} : undefined,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const JackpotFeedTable = ({minigameId}) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10});

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });
        apiService.get(`/api/minigames/stats/${minigameId}/jackpots?${params}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [minigameId, pagination]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            accessorKey: 'createdAt',
            header: 'Time',
            size: 170,
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—',
        },
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {
            accessorKey: 'eventOutput',
            header: 'Result',
            Cell: ({cell}) => {
                const v = cell.getValue();
                if (!v) return '—';
                if (v.combination) return v.combination;
                return JSON.stringify(v).substring(0, 60);
            },
        },
        {
            accessorKey: 'reward',
            header: 'Reward',
            Cell: ({cell}) => {
                const v = cell.getValue();
                if (!v || !Array.isArray(v) || v.length === 0) return '—';
                return v.map((r, i) => (
                    <Chip
                        key={i}
                        label={`${r.criteria?.amount || '?'} ${r.entity || ''}`}
                        size="small"
                        sx={{mr: 0.5}}
                    />
                ));
            },
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        rowCount,
        state: {pagination, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading jackpots'} : undefined,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const MinigameStatsDetailPage = () => {
    const {minigameId} = useParams();
    const [detail, setDetail] = useState(null);
    const [winsLeaderboard, setWinsLeaderboard] = useState([]);
    const [jackpotsLeaderboard, setJackpotsLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiService.get(`/api/minigames/stats/${minigameId}`),
            apiService.get(`/api/minigames/stats/${minigameId}/leaderboard?sort=wins`),
            apiService.get(`/api/minigames/stats/${minigameId}/leaderboard?sort=jackpots`),
        ])
            .then(([detailData, wins, jackpots]) => {
                setDetail(detailData);
                setWinsLeaderboard(wins);
                setJackpotsLeaderboard(jackpots);
            })
            .catch(err => setError(err.message || 'Failed to load statistics'))
            .finally(() => setLoading(false));
    }, [minigameId]);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!detail) return <Alert severity="info" sx={{m: 2}}>Minigame not found.</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Statistics: {detail.name}</Typography>

            <SummaryCard detail={detail}/>

            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid size={{xs: 12, md: 6}}>
                    <LeaderboardTable
                        title="Top 10 by Wins"
                        data={winsLeaderboard}
                        valueColumn="totalWins"
                        valueLabel="Wins"
                    />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <LeaderboardTable
                        title="Top 10 by Jackpots"
                        data={jackpotsLeaderboard}
                        valueColumn="totalJackpots"
                        valueLabel="Jackpots"
                    />
                </Grid>
            </Grid>

            <Box sx={{mb: 3}}>
                <Typography variant="h5" gutterBottom>Recent Plays</Typography>
                <PlayLogTable minigameId={minigameId}/>
            </Box>

            <Box sx={{mb: 3}}>
                <Typography variant="h5" gutterBottom>Recent Jackpots</Typography>
                <JackpotFeedTable minigameId={minigameId}/>
            </Box>

            <Button component={RouterLink} to="/minigames/stats" variant="outlined" startIcon={<ArrowBackIcon/>}>
                Back to Statistics
            </Button>
        </Box>
    );
};

MinigameStatsDetailPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
};

export default MinigameStatsDetailPage;
